import React, { useCallback, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from 'react-i18next';
import { isEmpty } from "heliocor-ui"

import {
    STORE_PARTY_DATA,
    REQUEST_SEND_DATA_PREAPPROVAL,
    REQUEST_SUBMIT_REQUEST,
    REQUEST_UPDATE_REQUESTED_ENTITY,
    SET_COMPANY_LOOKUP_STRUCTURE,
    SET_NOTIFICATION,
    DELETE_POST_ERROR
} from '../../store/cafe/actionTypes'
import { db } from "../../api/db"
import { customValidations, getFieldsIdsArray, isFieldVisible } from "../../utils/wizard"
import { StepsGenerator } from "./Steps.generator"
import { SpinnerLogo } from "../../common/SpinnerLogo"
import Wizard from "../../common/Wizard/Wizard"

const WizardCAFe = () => {

    const { t } = useTranslation()
    const authenticated = useSelector(store => store.config.authenticated)
    const email = useSelector(store => store.config.email)
    const partyType = useSelector(store => store.config.partyType)
    const documentsRequest = useSelector(store => store.settings.data)
    const customFields = useSelector(store => store.settings.customFields)
    const showStructure = useSelector(store => store.settings.structure)
    const showFields = useSelector(store => store.settings.showFields)
    const includeStructure = useSelector(store => store.settings.includeStructure)
    const showCategories = useSelector(store => store.settings.showCategories)
    const party = useSelector(store => store.party)
    const navigation = useSelector(store => store.navigation)
    const isRequest = useSelector(store => store.config.isRequest)
    const preapprovalCustomFields = useSelector(store => store.settings.previousPartyDetailsFields)
    const supportedCountries = useSelector(store => store.settings.supportedCountries)
    const supportedRegions = useSelector(store => store.settings.supportedRegions)
    const postErrors = useSelector(store => store.party.postErrors)
    const partyLoaded = useSelector(store => store.general.cafeStatus === 'PARTY_LOADED')
    const cafeReady = useSelector(store => store.general.cafeStatus === 'READY')
    const categories = useSelector(store => store.settings.fieldCategories || [])
    const kycTypes = useSelector(store => store.settings.kycTypes)
    const showYapilyStep = useSelector(store => store.settings.showYapilyStep)

    const dispatch = useDispatch()
    const storePartyData = (party) => dispatch({ type: STORE_PARTY_DATA, party })
    const sendDataPreApproval = () => dispatch({ type: REQUEST_SEND_DATA_PREAPPROVAL })
    const submitRequest = () => dispatch({ type: REQUEST_SUBMIT_REQUEST })
    const updateRequestedEntity = (values) => dispatch({ type: REQUEST_UPDATE_REQUESTED_ENTITY, values })
    const setStructureData = (structureData) => dispatch({ type: SET_COMPANY_LOOKUP_STRUCTURE, structureData })
    const sendNotification = (notification) => dispatch({ type: SET_NOTIFICATION, notification })
    const postErrorHandler = fieldId => dispatch({ type: DELETE_POST_ERROR, fieldId })

    const [errors, setErrors] = useState({})
    const [values, setValues] = useState(party?.data || {})
    const [customNavigationButtons, setCustomNavigationButtons] = useState([])

    const showKYCTypeStep = kycTypes?.length > 1

    const currentStepId = navigation.stepsList[navigation.stepIndex]
    const saveDataToStore = () => !isRequest && storePartyData({ data: { ...values } })

    const errorHandler = (currentErrors) => {

        if (Object.keys(currentErrors).length && !Object.values(currentErrors)[0].length) {
            setErrors(prev => {
                const errorUpdate = { ...prev }
                delete errorUpdate[Object.keys(currentErrors)]
                return errorUpdate
            })
        } else {
            setErrors(prev => ({ ...prev, ...currentErrors }))
        }
    }

    const generateWizardSteps = useCallback(() => {

        const cleanDependantFields = (fieldId, value) => {
            Object.values(customFields).forEach(cat => {
                cat.forEach(f => {
                    if (f.visibility?.[0].field === fieldId) {
                        const visible = isFieldVisible(f, value)
                        if (!visible) {
                            const fieldValue = f.type === 'CHECK' ? false : undefined
                            setValues(prev => ({ ...prev, [f.id]: fieldValue }))
                            cleanDependantFields(f.id)
                        }
                    }
                })
            })
        }

        const inputChangedHandler = (id, value) => {
            const fieldCat = steps && steps[partyType][navigation.stepIndex]?.id;
            const catFields = customFields[fieldCat]
            const type = catFields?.find(f => f.id === id)?.type

            if (['SELECT', 'CHECK'].includes(type)) {
                cleanDependantFields(id, value)
            }
            setValues(prev => ({ ...prev, [id]: value }))
        }

        const cleanValues = () => setValues(v => ({
            name: v.name,
            registeredAddressCountry: v.registeredAddressCountry,
            ownership: v.ownership,
            contactEmail: email
        }))
        const isLoading = !cafeReady
        const country = values?.registeredAddressCountry?.value
        const updateNavigationButtons = (buttons) => setCustomNavigationButtons(buttons)

        const wizardSteps = StepsGenerator({
            isLoading,
            inputChangedHandler,
            errorHandler,
            updateNavigationButtons,
            documentsRequest,
            cleanValues,
            customFields,
            showCategories,
            showFields,
            showStructure,
            country,
            supportedRegions,
            includeStructure,
            categories,
            values,
            t,
            showKYCTypeStep,
            showYapilyStep,
            errors
        })

        return wizardSteps

    }, [cafeReady, categories, customFields, documentsRequest, includeStructure, showCategories, showFields, showStructure, supportedRegions, t, values, email, navigation.stepIndex, partyType, steps, showKYCTypeStep, showYapilyStep])

    const getCompanyLookupDependencies = () => {
        let requestedCustomFieldsArray = getFieldsIdsArray(customFields)

        if (!isEmpty(preapprovalCustomFields)) {
            const preapprovalCustomFieldsArray = getFieldsIdsArray(preapprovalCustomFields)
            requestedCustomFieldsArray = [...requestedCustomFieldsArray, ...preapprovalCustomFieldsArray]
        }

        return ({
            setStructureData,
            supportedCountries,
            collectStructure: currentStepId !== 'STRUCTURE',
            customFieldsArray: requestedCustomFieldsArray,
            url: {
                checkCompanyExists: ({companyRegistrationNumber, companyCountry}) => 
                    `dokstor-cafe-api/api/self-onboarding/companies/registration-number/check?registration-number=${companyRegistrationNumber}&country=${companyCountry}`,
                checkCompanyHouse: ({ name, registeredAddressCountry, registrationNumber, region }) =>
                    `dokstor-cafe-api/api/search/companies/search?name=${name}&country=${registeredAddressCountry}&registration-number=${registrationNumber}${region ? `&region=${region}` : ''}`
            },
            sendNotification,
            db
        })
    }

    const hasNoPostErrors = () => {
        const { stepIndex, stepsList } = navigation
        const lastStep = stepIndex === stepsList.length - 1

        if (!postErrors || lastStep) return true

        const fields = customFields[stepsList[stepIndex]]
        return !postErrors?.some(e => fields?.find(f => f.id === e.field))
    }


    useEffect(() => {
        if (!authenticated) {
            setErrors({})
            setCustomNavigationButtons([])
        }

    }, [authenticated])

    useEffect(() => {
        partyType === 'COMPANY'
            ? setValues({ ...party.data, contactEmail: email || party.data.email })
            : setValues({ ...party.data, email: email || party.data.email })

    }, [partyLoaded, partyType, navigation.stepIndex, email, party.data])

    const steps = generateWizardSteps()
    const showSteps = !!steps && (!authenticated || showKYCTypeStep || (!showFields || (!isEmpty(categories) && !isEmpty(customFields))))

    return (showSteps
        ? <Wizard
            steps={steps[partyType] || []}
            saveDataToStore={saveDataToStore}
            customNavigationButtons={customNavigationButtons}
            formIsValid={isEmpty(errors) && hasNoPostErrors()}
            type={partyType}
            cleanErrors={() => setErrors({})}
            handleSubmit={isRequest ? submitRequest : sendDataPreApproval}
            customValidations={customValidations}
            values={values}
            companyLookUpInstructions={getCompanyLookupDependencies()}
            errors={errors}
            postErrors={postErrors}
            postErrorHandler={postErrorHandler}
            updateRequestedEntity={updateRequestedEntity}
            showKYCTypeStep={showKYCTypeStep}
            showYapilyStep={showYapilyStep}
        />
        : <SpinnerLogo dokstor />
    )
}

export { WizardCAFe }